import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  Check,
  KeyRound,
  UserPlus,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import type { User } from "../auth";
import {
  deviceAccounts,
  forgetDeviceAccount,
  rememberDeviceAccount,
} from "../device-accounts";
import { Button } from "./ui";

export function AccountSwitcher({ compact = false }: { compact?: boolean }) {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [accountName, setAccountName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [remembered, setRemembered] = useState(() => deviceAccounts());
  const current = status?.user;
  const targets = useMemo(
    () => remembered.filter((account) => account.id !== current?.id),
    [remembered, current?.id],
  );
  const selectedTarget = targets.find(
    (account) => account.id === selectedId,
  );

  useEffect(() => {
    if (!current) return;
    let known = deviceAccounts();
    if (!known.some((account) => account.id === current.id)) {
      rememberDeviceAccount(current, {
        persistent:
          window.localStorage.getItem("prive_login_name")?.toLowerCase() ===
          current.displayName.toLowerCase(),
      });
      known = deviceAccounts();
    }
    setRemembered(known);
  }, [current]);

  useEffect(() => {
    if (!targets.some((account) => account.id === selectedId))
      setSelectedId(targets[0]?.id || "");
  }, [targets, selectedId]);

  useEffect(() => {
    if (open && !targets.length) setAddingAccount(true);
  }, [open, targets.length]);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const switchAccount = useMutation({
    mutationFn: () =>
      api<{ user: User }>("/api/auth/switch", {
        method: "POST",
        body: JSON.stringify({
          ...(addingAccount
            ? { displayName: accountName.trim() }
            : { userId: selectedId }),
          password,
          rememberLogin: true,
        }),
      }),
    onSuccess: ({ user }) => {
      rememberDeviceAccount(user, { persistent: true });
      queryClient.clear();
      window.location.replace("/");
    },
    onError: (value) =>
      setError(value instanceof Error ? value.message : "账户切换失败"),
  });

  if (!current) return null;

  return (
    <>
      <button
        type="button"
        className={
          compact
            ? "topbar-settings account-switch-trigger compact"
            : "profile-chip account-switch-trigger"
        }
        aria-label="切换账户"
        onClick={() => {
          setError("");
          setPassword("");
          setRemembered(deviceAccounts());
          setAddingAccount(false);
          setAccountName("");
          setOpen(true);
        }}
      >
        <span>{current.displayName.slice(0, 1)}</span>
        {!compact && (
          <div>
            <strong>{current.displayName}</strong>
            <small>
              {current.role === "admin" ? "管理员 · 点击切换" : "点击切换账户"}
            </small>
          </div>
        )}
        {compact ? (
          <ArrowRightLeft size={16} />
        ) : (
          <ArrowRightLeft className="account-switch-arrow" />
        )}
      </button>

      {open && (
        <div
          className="account-switch-backdrop"
          onPointerDown={() => setOpen(false)}
        >
          <section
            className="account-switch-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-switch-title"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span>PRIVATE ACCOUNTS</span>
                <h2 id="account-switch-title">切换账户</h2>
                <p>每个账户拥有独立的任务、旅行、学习记录与个人偏好。</p>
              </div>
              <button
                type="button"
                aria-label="关闭切换账户"
                onClick={() => setOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="account-switch-body">
              <div className="current-account">
                <span>{current.displayName.slice(0, 1)}</span>
                <div>
                  <small>当前账户</small>
                  <b>{current.displayName}</b>
                </div>
                <Check />
              </div>
              {targets.length && !addingAccount ? (
                <>
                  <div
                    className="account-choice-list"
                    role="radiogroup"
                    aria-label="选择要切换的账户"
                  >
                    {targets.map((account) => (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selectedId === account.id}
                        className={selectedId === account.id ? "active" : ""}
                        key={account.id}
                        onClick={() => {
                          setSelectedId(account.id);
                          setPassword("");
                          setError("");
                        }}
                      >
                        <span>{account.displayName.slice(0, 1)}</span>
                        <div>
                          <b>{account.displayName}</b>
                          <small>
                            {account.role === "admin" ? "管理员" : "普通账户"}
                          </small>
                        </div>
                        {selectedId === account.id && <Check />}
                      </button>
                    ))}
                  </div>
                  <div className="field">
                    <label>
                      请输入 {selectedTarget?.displayName || "目标账号"} 的密码
                    </label>
                    <div className="input-icon">
                      <KeyRound />
                      <input
                        type="password"
                        value={password}
                        autoFocus
                        autoComplete="current-password"
                        onChange={(event) => setPassword(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && password)
                            switchAccount.mutate();
                        }}
                        placeholder="密码只用于本次切换"
                      />
                    </div>
                  </div>
                  {error && <div className="error-box">{error}</div>}
                  <Button
                    loading={switchAccount.isPending}
                    disabled={!selectedId || !password}
                    onClick={() => switchAccount.mutate()}
                  >
                    <ArrowRightLeft />
                    切换到 {selectedTarget?.displayName || "所选账号"}
                  </Button>
                  <button
                    type="button"
                    className="account-device-forget"
                    onClick={() => {
                      forgetDeviceAccount(selectedId);
                      const next = deviceAccounts();
                      setRemembered(next);
                      setSelectedId("");
                      setPassword("");
                      setError("");
                      if (!next.some((account) => account.id !== current.id))
                        setAddingAccount(true);
                    }}
                  >
                    只从这台设备移除该账号
                  </button>
                </>
              ) : (
                <>
                  <div className="account-switch-empty">
                    <UserRound />
                    <b>这台设备还没有记住其他账户</b>
                    <p>输入另一个账号及密码并验证成功后，它才会出现在本机切换列表中。</p>
                  </div>
                  <div className="field">
                    <label>另一个账号昵称</label>
                    <div className="input-icon">
                      <UserRound />
                      <input
                        value={accountName}
                        autoFocus
                        autoComplete="username"
                        onChange={(event) => setAccountName(event.target.value)}
                        placeholder="输入账号昵称"
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>账号密码</label>
                    <div className="input-icon">
                      <KeyRound />
                      <input
                        type="password"
                        value={password}
                        autoComplete="current-password"
                        onChange={(event) => setPassword(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && accountName.trim() && password)
                            switchAccount.mutate();
                        }}
                        placeholder="验证成功后在本机记住15天"
                      />
                    </div>
                  </div>
                  {error && <div className="error-box">{error}</div>}
                  <Button
                    loading={switchAccount.isPending}
                    disabled={!accountName.trim() || !password}
                    onClick={() => switchAccount.mutate()}
                  >
                    <UserPlus />
                    验证并切换账户
                  </Button>
                </>
              )}
              {targets.length > 0 && (
                <button
                  type="button"
                  className="account-add-another"
                  onClick={() => {
                    setAddingAccount((value) => !value);
                    setAccountName("");
                    setPassword("");
                    setError("");
                  }}
                >
                  <UserPlus />
                  {addingAccount ? "返回本机账户" : "添加另一个账户到这台设备"}
                </button>
              )}
              {current.role === "admin" && (
                <button
                  type="button"
                  className="account-admin-link"
                  onClick={() => {
                    setOpen(false);
                    navigate("/admin");
                  }}
                >
                  <ShieldCheck />
                  <span>
                    <b>打开账户管理后台</b>
                    <small>创建账户、停用成员或重置密码</small>
                  </span>
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
